import React, { type MouseEvent, useCallback, useContext, useEffect, useState } from 'react';

import { Table, TableBody, TableCell, TableContainer, TableRow, styled } from '@mui/material';

import AttendanceEditForm from './AttendanceEditForm';
import AttendanceTableHead from './AttendanceTableHead';
import AttendanceTableRow from './AttendanceTableRow';
import NoAttendanceView from './NoAttendanceView';
import { type ClassSessionView, type AttendanceTableColumns, type Order } from './types';
import { useAttendanceData } from './useAttendanceData';
import { AttendanceContext, View } from '../../context/AttendanceContext';
import { getComparator } from '../../utils/sortingUtils';
import Modal from '../Modal';

const AttendanceFooterCell = styled(TableCell)`
    font-weight: bold;
`;

const AttendanceTable: React.FC = () => {
    const {
        view,
        setSelectedStudent,
        setSelectedSession,
        setTotals,
        markAttendance,
        attendanceSettings,
        t
    } = useContext(AttendanceContext);
    const { sessionRows, studentRows } = useAttendanceData();

    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<AttendanceTableColumns>(() => (view === View.Session ? 'sortName' : 'sortDate'));
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (studentRows.length < 1) return;

        const filteredStudentRows: ClassSessionView[] = studentRows.filter((object) => object.found);
        const present = filteredStudentRows.filter((i) => i.attendance).length;
        const tardy = filteredStudentRows.filter((i) => i.tardiness).length;
        const absent = filteredStudentRows.length - present;

        setTotals({ present, absent, tardy });
    }, [setTotals, studentRows]);

    useEffect(() => {
        switch (view) {
            case View.Session:
                setOrderBy('sortName');
                break;
            case View.Student:
                setOrderBy('sortDate');
                break;
            default:
                break;
        }
    }, [view]);

    const handleRowClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>, selectedStudent, selectedSession) => {
            setSelectedStudent(selectedStudent);
            setSelectedSession(selectedSession);

            setOpen(true);
        },
        [setSelectedSession, setSelectedStudent]
    );

    const handleMenuClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleRequestSort = useCallback(
        (event: React.MouseEvent<unknown>, property: AttendanceTableColumns) => {
            const sortProperty = property === 'name' ? 'sortName' :
                                 property === 'date' ? 'sortDate' : property;

            const isAsc = orderBy === sortProperty && order === 'asc';

            setOrder(isAsc ? 'desc' : 'asc');
            setOrderBy(sortProperty);
        },
        [order, orderBy]
    );

    const renderTableBody = useCallback(() => {
        let sorted;
        switch (view) {
            case View.Session: {
                sorted = studentRows.slice().sort(getComparator(order, orderBy));
                sorted.forEach((row) => {
                    if (row.score) {
                        delete row.score;
                    }
                });
                break;
            }
            case View.Student: {
                sorted = sessionRows.slice().sort(getComparator(order, orderBy));
                break;
            }
            default:
                sorted = [];
                break;
        }
        sorted = sorted.filter((object) => object.found);
        return sorted.map((row, index) => <AttendanceTableRow key={index} row={row} onClick={handleRowClick} />);
    }, [handleRowClick, order, orderBy, sessionRows, studentRows, view]);

    const renderTableFooter = useCallback(() => {
        if (view === View.Student && attendanceSettings?.showAttendanceGrade) {
            const sorted = sessionRows.slice().filter((object) => object.found); // sort and filter;
            // compute the student's score based on attendance
            const present = sorted.filter((i) => i.attendance && !i.tardiness).length;
            const tardy = sorted.filter((i) => i.tardiness).length;
            const totalSessions = sorted.length;
            const score = ((( present + tardy * (attendanceSettings?.attendanceGradeTardyScorePercent ?? 0.8)) / totalSessions) * 100).toFixed(2);
            return (
                <TableRow>
                    <AttendanceFooterCell colSpan={7} align={'right'}>
                        {t('score')}: {score}%
                    </AttendanceFooterCell>
                </TableRow>
            );
        } else {
            return [];
        }
    }, [sessionRows, view, t, attendanceSettings]);

    if (studentRows.length < 1 && sessionRows.length < 1) {
        return <NoAttendanceView />;
    }

    return (
        <TableContainer>
            <Table aria-labelledby="student-attendance-table" size="medium">
                <AttendanceTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                <TableBody>{renderTableBody()}</TableBody>
                {renderTableFooter()}
            </Table>

            <Modal isOpen={open} onClose={handleMenuClose}>
                <AttendanceEditForm onCancel={handleMenuClose} onFinish={markAttendance} />
            </Modal>
        </TableContainer>
    );
};

export default AttendanceTable;
